import { macroCondition, isTesting } from '@embroider/macros';
import Service, { service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import { resetDemoReportDownloads } from 'tio-employee/demo/report-fixtures';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import ENV from 'tio-employee/config/environment';
import { resolveDemoScenario } from 'tio-employee/demo/registry';
import { USER_ID as DEMO_USER_ID } from 'tio-employee/demo/identity-fixtures';
import { demoUploadBatchStatus } from 'tio-employee/demo/loan-documents-fixtures';
import { rememberDemoUploadBody } from 'tio-employee/demo/upload-fixtures';
import { demoXhrClass, fileBodySize, type DemoXhrAnswer } from 'tio-employee/utils/demo-xhr-guard';
import type { FetchMatcher } from 'tio-data/demo/types';
import type EmployeeCableService from 'tio-employee/services/cable';
import type FeaturesService from 'tio-common/services/features';
import { CAPABILITY_SOURCE_RANK, type CapabilitySource } from 'tio-common/services/features';
import type SessionContextService from 'tio-employee/services/session-context';
import {
  DEMO_CLIENT_LIST,
  DEMO_PRODUCT_LIST,
  DEMO_SESSION_ROLE_LIST,
  DEMO_TA_PAYMENT_MODEL_LIST,
  DEMO_TAS_SCENARIO_OPTION_LIST,
  DEMO_DEBT_LEVEL_LIST,
  DEMO_DOCUMENT_STATUS_LIST,
  DEMO_USER_SCENARIO_LIST,
  applyDemoSession,
  exitDemoSession,
  isDemoSessionArmed,
  noteDemoDestination,
  resolveDemoEntryHref,
  getCurrentDemoClientId,
  getCurrentDemoProductIds,
  getCurrentDemoRoleId,
  getCurrentDemoTaPaymentModelId,
  getCurrentDemoDebtLevelId,
  getCurrentDemoUserScenarioId,
  getCurrentDemoPslfScenarioId,
  getCurrentDemoDocumentStatusId,
  getCurrentDemoScenario,
  isFeatureEnabled,
  getDemoData,
  getDemoProduct,
  getDemoSessionRole,
  getDemoTaPaymentModel,
  getDemoTasScenarioOption,
  getDemoDebtLevel,
  getDemoDocumentStatus,
  getDemoUserScenario,
  isReimaginedDemoMode,
  isPslfEligibleClient,
  selectionHasTuitionAssistance,
  type DemoClientId,
  type DemoProductId,
  type DemoSessionRoleId,
  type DemoSessionSelection,
  type DemoSessionSelectionSnapshot,
  type DemoTaPaymentModelId,
  type DemoTasScenarioId,
  type DemoDebtLevelId,
  type DemoDocumentStatusId,
  type DemoScenario,
  type DemoUserScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

const NULL_BODY_STATUSES = new Set([204, 205, 304]);
const DEMO_PROCESSING_MS = macroCondition(isTesting()) ? 50 : 3_000;

/**
 * Relationship types treated as internal TIO staff for the (currently
 * disabled) production gate. The precise definition is intentionally
 * conservative — superuser + TIO approver — and will be finalised when
 * `demoModeRequireStaffRoleInProd` is turned on.
 */
const STAFF_RELATIONSHIP_TYPES = new Set<string>([
  'TAS.Admin.SUPERUSER',
  'TAS.Approver.TIO_APPROVER',
]);

/**
 * The capability keys demo "owns" — the active scenario's product/feature flags
 * (career-pathways `DemoScenario.features`). The demo capability source speaks
 * only for these (and only while a session is active); it abstains on everything
 * else — including `demoMode` itself, which {@link DemoModeService.isAvailable}
 * reads, so that key keeps resolving from the static flag tier (no re-entrancy).
 */
const DEMO_SCENARIO_CAPABILITY_KEYS = new Set<string>([
  'tuitionAssistance',
  'studentLoanRepayment',
  'careerPathways',
  'financialWellness',
  'pslf',
  'employerMatch',
]);

/**
 * Owns the demo availability gate and all session-state reads/writes.
 * Consumers use this service instead of reading localStorage, URL parameters or environment directly.
 */
export default class DemoModeService extends Service {
  @service declare features: FeaturesService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;
  @service declare cable: EmployeeCableService;

  #originalFetch?: typeof fetch;
  #originalXhr?: typeof XMLHttpRequest;
  #demoBatchTimers = new Set<ReturnType<typeof setTimeout>>();

  private noteTransition = (transition: Transition) => {
    noteDemoDestination(transition.to?.name);
  };

  constructor(...args: ConstructorParameters<typeof Service>) {
    super(...args);
    // Seeded FIRST: the initial routeWillChange predates this service, and the
    // interceptor gate below reads a destination-aware signal.
    if (isDemoSessionArmed()) {
      try {
        noteDemoDestination(
          this.router.recognize(window.location.pathname + window.location.search)?.name
        );
      } catch {
        // Stub/windowless router: the first transition sets the destination.
      }
    }
    this.#installFetchInterceptor();
    this.#installXhrGuard();
    // Availability gates it so a real user keeps the cable; the authenticated route re-checks once the role loads.
    if (this.isActive || this.isArmed) {
      this.cable.disabled = true;
    }
    registerDestructor(this, () => {
      for (const timer of this.#demoBatchTimers) clearTimeout(timer);
      this.#demoBatchTimers.clear();
    });
    // Real sessions pay nothing here. `on`/`off` are absent on test stubs.
    if (
      (isDemoSessionArmed() || isReimaginedDemoMode()) &&
      typeof this.router.on === 'function' &&
      typeof this.router.off === 'function'
    ) {
      this.router.on('routeWillChange', this.noteTransition);
      // didChange corrects an aborted transition's stale destination.
      this.router.on('routeDidChange', this.noteTransition);
      registerDestructor(this, () => {
        this.router.off('routeWillChange', this.noteTransition);
        this.router.off('routeDidChange', this.noteTransition);
      });
    }
  }

  /**
   * Installs a demo-only fetch interceptor, restored on service teardown.
   * Active sessions return fixtures or inert responses for API requests; inactive sessions pass through.
   * Installation reads raw storage/URL state because feature gates fail closed before seeding;
   * serving/blocking rechecks isActive.
   * File uploads use XHR, which the XHR guard covers; the constructor disables the cable.
   */
  #installFetchInterceptor(): void {
    if (typeof window === 'undefined' || this.#originalFetch) {
      return;
    }
    if (!isDemoSessionArmed() && !isReimaginedDemoMode()) {
      return;
    }
    const original = window.fetch.bind(window);
    this.#originalFetch = original;

    const intercept: typeof fetch = (input, init) => {
      try {
        const demoResponse = this.#demoResponseFor(input, init);
        if (demoResponse) {
          return Promise.resolve(demoResponse);
        }
      } catch {
        // Never let demo interception break a real request.
      }
      return original(input, init);
    };

    window.fetch = intercept;
    registerDestructor(this, () => {
      if (this.#originalFetch && window.fetch === intercept) {
        window.fetch = this.#originalFetch;
        resetDemoReportDownloads();
      }
    });
  }

  // Same gate as the fetch seam; answers file uploads locally, the API origin inertly, and passes the rest through.
  #installXhrGuard(): void {
    if (typeof window === 'undefined' || this.#originalXhr) {
      return;
    }
    if (!isDemoSessionArmed() && !isReimaginedDemoMode()) {
      return;
    }
    const native = window.XMLHttpRequest;
    this.#originalXhr = native;
    const guarded = demoXhrClass(native, (method, url, body) => this.#xhrAnswer(method, url, body));
    window.XMLHttpRequest = guarded;
    registerDestructor(this, () => {
      if (window.XMLHttpRequest === guarded) {
        window.XMLHttpRequest = native;
      }
    });
  }

  #xhrAnswer(method: string, url: string, body: unknown): DemoXhrAnswer | undefined {
    if (!this.isActive) {
      return undefined;
    }
    const size = method === 'PUT' || method === 'POST' ? fileBodySize(body) : undefined;
    if (size !== undefined) {
      this.#recordLocalUpload(method, url, body);
      return { kind: 'upload', total: size };
    }
    const inert = this.#inertApiAnswer(url, method);
    return inert ? { kind: 'inert', ...inert } : undefined;
  }

  /** File uploads the XHR guard answered locally during an active demo session (mirrored to `window.__demoLocalUploads`). */
  get localUploads(): ReadonlyArray<{ method: string; url: string }> {
    return this.#localUploads;
  }

  #localUploads: Array<{ method: string; url: string }> = [];

  #recordLocalUpload(method: string, url: string, body: unknown): void {
    const entry = { method, url };
    this.#localUploads.push(entry);
    const mirror = window as Window & { __demoLocalUploads?: Array<typeof entry> };
    (mirror.__demoLocalUploads ??= []).push(entry);
    rememberDemoUploadBody(url, body);
  }

  #demoResponseFor(input: RequestInfo | URL, init?: RequestInit): Response | undefined {
    let url: string;
    let method: string | undefined;
    if (typeof input === 'string') {
      url = input;
      method = init?.method;
    } else if (input instanceof URL) {
      url = input.href;
      method = init?.method;
    } else {
      url = input.url;
      method = init?.method ?? input.method;
    }

    const verb = (method ?? 'GET').toUpperCase();
    // The match attempt gets its own guard: an exception inside scenario
    // resolution / fixture building must NOT disable the network-free block
    // below (the interceptor's outer catch used to silently turn any such
    // exception into full passthrough — every fixture AND the guarantee
    // vanished at once, with nothing in the console).
    let hit: { match: FetchMatcher; body?: unknown } | undefined;
    try {
      hit = this.#demoFetchMatch(url, verb, init?.body);
    } catch (error) {
      this.#warnResolveFailure(error);
      hit = undefined;
    }
    if (hit) {
      const { match } = hit;
      let payload: string | undefined;
      let body: unknown;
      try {
        body = typeof match.json === 'function' ? match.json(url, hit.body) : match.json;
        payload = JSON.stringify(body);
      } catch (error) {
        // A throwing fixture must be blocked, never passed through to the network by the outer catch.
        this.#warnResolveFailure(error);
        return this.#blockedApiResponse(url, verb);
      }
      const status = match.status ?? 200;
      // The Response constructor throws on a body with a null-body status such as 204.
      if (NULL_BODY_STATUSES.has(status)) {
        return new Response(null, { status });
      }
      this.#scheduleDemoBatchStatus(url, verb, body);
      return new Response(payload, { status, headers: { 'Content-Type': 'application/json' } });
    }
    return this.#blockedApiResponse(url, verb);
  }

  #scheduleDemoBatchStatus(url: string, verb: string, body: unknown): void {
    if (verb !== 'POST' || !/\/observable-documents(\?|$)/.test(url)) return;
    const resource = (body as { data?: { id?: unknown } } | null)?.data;
    if (typeof resource?.id !== 'string') return;
    const status = demoUploadBatchStatus(resource.id, this.currentSelection);
    if (!status) return;
    const timer = setTimeout(() => {
      this.#demoBatchTimers.delete(timer);
      if (this.isActive) this.cable.emitDemoBatchStatus(status.batchId, status.message);
    }, DEMO_PROCESSING_MS);
    this.#demoBatchTimers.add(timer);
  }

  #warnedResolveFailures = new Set<string>();

  /** A broken scenario resolve is a demo-breaking bug — say so, loudly, once per message. */
  #warnResolveFailure(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    if (this.#warnedResolveFailures.has(message)) {
      return;
    }
    this.#warnedResolveFailures.add(message);
    console.error(
      `[demo] scenario resolution threw — fixtures are NOT being served (demo is broken): ${message}`,
      error
    );
  }

  /**
   * The live API origin the network-free guarantee protects. `null` when the
   * environment defines no resolvable apiHost (the test environment uses ''
   * so MSW can intercept relative URLs) — with no API origin the block is
   * inert and the interceptor is a pure fixture layer. Overridable so tests
   * can point the guarantee at a sentinel origin.
   */
  get apiOrigin(): string | null {
    try {
      return new URL(ENV.apiHost).origin;
    } catch {
      return null;
    }
  }

  /**
   * Requests to the live API origin that were served an inert response while
   * a demo session was active, because no fetch-matcher (and no upstream
   * request-handler fixture) claimed them. Each one is a FIXTURE GAP, not an
   * error: the demo stayed network-free, and the worst case is an empty
   * widget. The demo-coverage audit reads this (via
   * `window.__demoBlockedRequests`) to report gaps per route.
   */
  get blockedApiRequests(): ReadonlyArray<{ method: string; url: string }> {
    return this.#blockedApiRequests;
  }

  #blockedApiRequests: Array<{ method: string; url: string }> = [];
  #warnedBlockedEndpoints = new Set<string>();

  /**
   * Blocks unmatched requests to the live API in active demos: reads return empty collections,
   * writes return inert 204 responses and persist nothing. Fix missing fixtures rather than relaxing this boundary.
   * Non-API origins and inactive sessions pass through unchanged.
   */
  #inertApiAnswer(url: string, verb: string): { status: number; body: string | null } | undefined {
    if (!this.isActive) {
      return undefined;
    }
    const apiOrigin = this.apiOrigin;
    if (!apiOrigin) {
      return undefined;
    }
    try {
      if (new URL(url, window.location.origin).origin !== apiOrigin) {
        return undefined;
      }
    } catch {
      return undefined;
    }

    this.#recordBlockedRequest(verb, url);
    if (verb === 'GET' || verb === 'HEAD') {
      // HEURISTIC over this API's conventional URL shape (JSON:API itself
      // prescribes no URL structure): an ODD number of path segments is
      // treated as a collection (`/employees`, `/users/1/summarize-loans`),
      // an EVEN number as a single resource (`/employees/1`). Good enough to
      // keep blocked pages rendering; a page that needs better gets a real
      // fixture. Single-resource reads must get `data: null`
      // — an empty ARRAY materializes as "[] is not a ReactiveResource or
      // Model known to WarpDrive" and error-pages the route (seen live on
      // /pslf/dashboard and /admin/employees/add), where `data: null` resolves
      // to a coherent empty record read.
      let segments: number;
      try {
        segments = new URL(url, window.location.origin).pathname.split('/').filter(Boolean).length;
      } catch {
        segments = 1;
      }
      const data = segments % 2 === 0 ? null : [];
      return { status: 200, body: JSON.stringify({ data, meta: { demoBlocked: true } }) };
    }
    return { status: 204, body: null };
  }

  // The fetch seam's form of the inert answer.
  #blockedApiResponse(url: string, verb: string): Response | undefined {
    const inert = this.#inertApiAnswer(url, verb);
    if (!inert) {
      return undefined;
    }
    return new Response(inert.body, {
      status: inert.status,
      headers: inert.body === null ? undefined : { 'Content-Type': 'application/json' },
    });
  }

  /**
   * Records a blocked request for the audit (window mirror) and warns once
   * per distinct endpoint so a fixture gap is visible in the console without
   * a polling call flooding it.
   */
  #recordBlockedRequest(method: string, url: string): void {
    const entry = { method, url };
    this.#blockedApiRequests.push(entry);
    const mirror = window as Window & { __demoBlockedRequests?: Array<typeof entry> };
    (mirror.__demoBlockedRequests ??= []).push(entry);

    const endpoint = `${method} ${url.split('?')[0] ?? url}`;
    if (!this.#warnedBlockedEndpoints.has(endpoint)) {
      this.#warnedBlockedEndpoints.add(endpoint);
      console.warn(`[demo] network-free: blocked unmatched API request (fixture gap?) ${endpoint}`);
    }
  }

  /**
   * Finds the active scenario's fetch-matcher for a raw `url`+`method` while a
   * demo session is active, else `undefined`. The single matching point shared
   * by the fetch interceptor (which also honours the matcher's `status`) and
   * `demoJsonFor` (the unit-test probe of this seam).
   */
  #demoFetchMatch(
    url: string,
    method: string,
    rawBody?: unknown
  ): { match: FetchMatcher; body?: unknown } | undefined {
    if (!this.isActive) {
      return undefined;
    }
    const verb = method.toUpperCase();
    let parsed: { body: unknown } | undefined;
    for (const fetchMatcher of this.resolveFetchMatchers()) {
      if (fetchMatcher.method && fetchMatcher.method.toUpperCase() !== verb) {
        continue;
      }
      const urlMatched = fetchMatcher.urlMatches
        ? fetchMatcher.urlMatches(url)
        : fetchMatcher.urlIncludes
          ? url.includes(fetchMatcher.urlIncludes)
          : false;
      if (!urlMatched) {
        continue;
      }
      if (!fetchMatcher.bodyMatches) {
        return { match: fetchMatcher };
      }
      parsed ??= { body: this.#parseJsonBody(rawBody) };
      if (fetchMatcher.bodyMatches(parsed.body)) {
        return { match: fetchMatcher, body: parsed.body };
      }
    }
    return undefined;
  }

  /** The active scenario's fetch matchers; overridable so tests can inject a fixture the registry never builds. */
  resolveFetchMatchers(): FetchMatcher[] {
    return resolveDemoScenario(this.currentSelection)?.fetchMatchers ?? [];
  }

  #parseJsonBody(raw: unknown): unknown {
    if (typeof raw !== 'string') {
      return undefined;
    }
    try {
      return JSON.parse(raw) as unknown;
    } catch {
      return undefined;
    }
  }

  /**
   * Returns the demo response body for a raw `url`+`method` while a demo session
   * is active (matched against the active scenario's fetch-matchers), or the
   * `undefined` sentinel when there is no active demo match. The body is `unknown`
   * because it mirrors arbitrary backend JSON; `FetchMatcher.json` is always
   * defined, so `undefined` unambiguously means "no match".
   *
   * No production code calls this anymore: the WarpDrive request migration
   * removed the custom adapters whose raw `ajax` used to consult it, so every
   * HTTP path now reaches the fetch interceptor above (or the request-manager
   * demo handler). Retained as the direct unit-test probe of the fetch-matcher
   * seam (tests/unit/services/demo-mode-test.ts and the fixture tests) — if it
   * is ever removed, those tests must drive the interceptor instead.
   */
  demoJsonFor(url: string, method = 'GET', body?: string): unknown {
    const hit = this.#demoFetchMatch(url, method, body);
    if (!hit) {
      return undefined;
    }
    const { match } = hit;
    return typeof match.json === 'function' ? match.json(url, hit.body) : match.json;
  }

  /**
   * Expose demo state as a {@link CapabilitySource} for the features service
   * (highest rank). While a demo session is active it answers the active
   * scenario's product/feature flags; otherwise — and for any non-scenario key —
   * it abstains, so `features.can(...)` falls through to beta/flag/default.
   */
  asCapabilitySource(): CapabilitySource {
    return {
      id: 'demo',
      rank: CAPABILITY_SOURCE_RANK.demo,
      resolve: (key) => {
        if (!this.isActive || !DEMO_SCENARIO_CAPABILITY_KEYS.has(key as string)) {
          return undefined;
        }
        return isFeatureEnabled(key as Parameters<typeof isFeatureEnabled>[0]);
      },
    };
  }

  /**
   * Whether the demo system is reachable in this session. Fails safe to
   * `false` when the master flag is absent. Enabled in every environment
   * today; flip `featureFlags.demoModeRequireStaffRoleInProd` to restrict
   * production to internal staff.
   */
  get isAvailable(): boolean {
    try {
      if (!this.features.isEnabled('demoMode')) {
        return false;
      }
      if (this.features.tioEnvironment !== 'production') {
        return true;
      }
      if (!this.features.isEnabled('demoModeRequireStaffRoleInProd')) {
        return true;
      }
      return this.isStaffUser;
    } catch {
      // `features.isEnabled` throws if the features service has not been seeded
      // with ENV yet. Because this getter gates the root application template,
      // fail closed (demo unavailable) rather than crash the app.
      return false;
    }
  }

  /** True when a demo session is currently applied (and demo mode is allowed). */
  get isActive(): boolean {
    return this.isAvailable && isReimaginedDemoMode();
  }

  /**
   * True when a sticky demo session is armed, even if the CURRENT route does
   * not render demo data (see {@link isActive} — the sticky flag is
   * path-scoped). `isArmed && !isActive` is the "paused on a non-demoable
   * route, showing real data" state the demo pill warns about.
   */
  get isArmed(): boolean {
    return this.isAvailable && isDemoSessionArmed();
  }

  /**
   * Exit the demo session entirely: wipe all sticky demo state and reload the
   * current route with every demo param stripped. The single exit path used by
   * the demo pill, the picker footer, and the reimagined header toggle.
   */
  exit(): void {
    exitDemoSession();
  }

  /** Navigates a paused session to the current app’s role-specific entry; its sticky flag reactivates demo on arrival. */
  resume(): void {
    if (typeof window === 'undefined') {
      return;
    }
    window.location.assign(resolveDemoEntryHref(window.location.href, getCurrentDemoRoleId()));
  }

  #pickerOpener?: () => void;

  /**
   * The globally-rendered picker (DemoDataShortcut) registers its modal opener
   * here for its lifetime, so any surface with this service — e.g. the
   * reimagined header's "Demo settings" launcher — can open THE one picker
   * instead of growing its own control surface. Returns the unregister
   * cleanup; registration is last-writer-wins (the picker renders once).
   */
  registerPickerOpener(open: () => void): () => void {
    this.#pickerOpener = open;
    return () => {
      if (this.#pickerOpener === open) {
        this.#pickerOpener = undefined;
      }
    };
  }

  /** Open the demo picker modal. A no-op when the picker is not rendered. */
  openPicker(): void {
    this.#pickerOpener?.();
  }

  /**
   * True only when THIS session IS the fabricated demo identity — the `/me`
   * sentinel ({@link DEMO_USER_ID}) that has no live backend record. Distinct from
   * {@link isActive}: the sticky demo flag is path-scoped, so `isActive` can be true
   * for a REAL logged-in user (real `/me`) on a demo-flagged path — that user must
   * still reach the live API. Gate "would this hit the API with a non-live id?"
   * decisions on this, NOT on `isActive`.
   */
  get isDemoSessionUser(): boolean {
    return this.sessionContext.user?.id === DEMO_USER_ID;
  }

  /** Whether the current role is internal TIO staff (used by the prod gate). */
  get isStaffUser(): boolean {
    const relationshipType = this.sessionContext.currentRole?.relationshipType;
    return relationshipType ? STAFF_RELATIONSHIP_TYPES.has(relationshipType) : false;
  }

  get clients() {
    return DEMO_CLIENT_LIST;
  }

  get products() {
    return DEMO_PRODUCT_LIST;
  }

  get roles() {
    return DEMO_SESSION_ROLE_LIST;
  }

  get userScenarios() {
    return DEMO_USER_SCENARIO_LIST;
  }

  get taPaymentModels() {
    return DEMO_TA_PAYMENT_MODEL_LIST;
  }

  get tasScenarios() {
    return DEMO_TAS_SCENARIO_OPTION_LIST;
  }

  get debtLevels() {
    return DEMO_DEBT_LEVEL_LIST;
  }

  get documentStatuses() {
    return DEMO_DOCUMENT_STATUS_LIST;
  }

  /**
   * The active scenario's benefit switches, or `null` outside a demo session.
   * The shell's benefit rows and the beta home read this so a demo follows the
   * scenario rather than the fixture identity, which always carries a paying plan.
   */
  get scenarioFeatures(): DemoScenario['features'] | null {
    return this.isActive ? getCurrentDemoScenario().features : null;
  }

  get currentSelection(): DemoSessionSelectionSnapshot {
    return {
      clientId: getCurrentDemoClientId(),
      productIds: getCurrentDemoProductIds(),
      roleId: getCurrentDemoRoleId(),
      userScenarioId: getCurrentDemoUserScenarioId(),
      taPaymentModelId: getCurrentDemoTaPaymentModelId(),
      pslfScenarioId: getCurrentDemoPslfScenarioId(),
      debtLevelId: getCurrentDemoDebtLevelId(),
      documentStatusId: getCurrentDemoDocumentStatusId(),
    };
  }

  clientName(clientId: DemoClientId): string {
    return getDemoData(clientId).client.name;
  }

  productName(productId: DemoProductId): string {
    return getDemoProduct(productId).name;
  }

  roleName(roleId: DemoSessionRoleId): string {
    return getDemoSessionRole(roleId).name;
  }

  userScenarioName(userScenarioId: DemoUserScenarioId): string {
    return getDemoUserScenario(userScenarioId).name;
  }

  taPaymentModelName(taPaymentModelId: DemoTaPaymentModelId): string {
    return getDemoTaPaymentModel(taPaymentModelId).name;
  }

  tasScenarioName(tasScenarioId: DemoTasScenarioId): string {
    return getDemoTasScenarioOption(tasScenarioId).name;
  }

  debtLevelName(debtLevelId: DemoDebtLevelId): string {
    return getDemoDebtLevel(debtLevelId).name;
  }

  documentStatusName(documentStatusId: DemoDocumentStatusId): string {
    return getDemoDocumentStatus(documentStatusId).name;
  }

  /**
   * Whether the selected benefit set includes Tuition Assistance. The TA payment
   * model is a TA-only axis (assistance manner + direct-pay settings), so the
   * picker only offers it when TA is among the selected products.
   */
  isTaSelected(productIds: DemoProductId[]): boolean {
    return selectionHasTuitionAssistance(productIds);
  }

  /**
   * Whether the selected persona's employer is PSLF-eligible (non-profit or
   * government). For-profit personas can't qualify, so the picker disables the
   * PSLF product for them (and personaMaskedFeatures masks it in the session).
   */
  isPslfEligibleClient(clientId: DemoClientId): boolean {
    return isPslfEligibleClient(clientId);
  }

  /** Apply a demo session (persists the selection and reloads into demo mode). */
  apply(selection: DemoSessionSelection): void {
    applyDemoSession(selection);
  }
}
